<template>
  <div>
    <vue-loading v-if="loading"></vue-loading>
    <div v-else>
      <h1>
        Survey for {{ survey.clientLastName }} {{ survey.clientFirstName }}
      </h1>

      <transition name="fade" mode="out-in">
        <div v-if="hasDefaultMode" class="row" key="roomPanel">
          <div class="col-lg-12 col-sm-12">
            <div class="qm-panel">
              <h2>Rooms</h2>
              <rooms v-bind:surveyId="session.surveyId"></rooms>
            </div>
          </div>
          <div class="col-lg-12 col-sm-12">
            <div class="qm-panel">
              <router-link :to="{ name: 'resources' }" tag="button" class="btn btn-turquois">
                <i class="fa fa-fw fa-pencil"></i> Resources
                <span v-if="countResources > 0" class="badge badge-default badge-pill qm-resource-count">{{ countResources }}</span>
              </router-link>
            </div>
          </div>
        </div>
        <div v-else class="row" key="modePanel">
          <div class="col-lg-12 col-sm-12">
            <div class="qm-panel">
              <h2>Rooms</h2>
              <div class="alert alert-warning">
                <i class="fa fa-fw fa-exclamation-triangle"></i>
                To proceed with registering rooms and survey items, please first select the default modeway for this survey.
              </div>
              <table class="table">
                <tr>
                  <td width="250">
                    <label for="qm-survey-default-mode-select">Default Mode</label>
                  </td>
                  <td>
                    <select v-model="selectedDefaultMode" v-on:change="selectDefaultMode" class="custom-select" name="qm-survey-default-mode-select" id="qm-survey-default-mode-select">
                      <option v-for="mode in modesOptions" v-bind:value="mode.id">{{ mode.text }}</option>
                    </select>
                  </td>
                </tr>
              </table>
            </div>
          </div>
        </div>
      </transition>

      <div class="row">
        <div class="col-lg-12 col-sm-12 qm-panel-mb-1">
          <div class="qm-panel">
            <h2>Details</h2>
            <table class="table">
              <tbody>
              <tr>
                <td width="250">Survey ID</td>
                <td>{{ survey.surveyId }}</td>
              </tr>
              <tr>
                <td>Client Name</td>
                <td>{{ survey.clientLastName }} {{ survey.clientFirstName }}</td>
              </tr>
              <tr>
                <td>Survey Date & Time</td>
                <td>{{ survey.surveyDate | formatDate }} {{ survey.surveyTime | formatTime }}</td>
              </tr>
              <tr>
                <td>Survey Signed</td>
                <td>
                  <div v-if="survey.signed">
                    <router-link :to="{ name: 'survey-signature' }"><i class="fa fq-fw fa-check-square qm-symbol-turquois"></i></router-link>
                    <img v-bind:src="survey.signatureDataUrl" alt="Signature" class="qm-signature-preview">
                  </div>
                  <div v-else>
                    <i class="fa fa-fw fa-close qm-symbol-grey"></i>
                    <router-link :to="{ name: 'survey-signature' }" tag="button" class="btn btn-sm btn-turquois"><i class="fa fa-fw fa-key"></i> Signature</router-link>
                  </div>
                </td>
              </tr>
              <tr>
                <td>Survey Closed</td>
                <td>
                  <i v-if="survey.closed" class="fa fa-check-square qm-symbol-turquois"></i>
                  <div v-else>
                    <button v-if="survey.signed" v-on:click="closeSurveyConfirm" type="button" class="btn btn-warning qm-survey-edit-button">

                      <span v-if="confirmClose">
                        <i class="fa fa-fw fa-arrow-circle-left"></i>
                        Back
                      </span>
                      <span v-else>
                        <i class="fa fa-fw fa-times-circle"></i>
                        Close Survey
                      </span>
                    </button>
                    <button v-if="confirmClose" id="close-survey-button" v-on:click="closeSurvey" type="button" class="btn btn-success qm-survey-edit-button">
                      <i class="fa fa-fw fa-check-circle"></i>
                      Confirm
                    </button>
                    <p> </p>
                    <p v-if="survey.signed && confirmClose">
                      <i class="fa fa-fw fa-exclamation-triangle"></i>
                      <em>
                        Once closed, please transmit the survey to the QualiMover server through the Synchronization > Export process.
                      </em>
                    </p>
                  </div>
                </td>
              </tr>
              <tr>
                <td>Surveyor</td>
                <td>{{ survey.surveyor }}</td>
              </tr>
              </tbody>
            </table>
            <router-link :to="{ name: 'survey-detail' }" tag="button" class="btn btn-turquois">
              <i class="fa fa-fw fa-pencil"></i>
              Edit Details
            </router-link>
          </div>
        </div>
      </div>

      <transition name="fade">
        <div v-if="hasDefaultMode" class="row">
          <div class="col-lg-12 col-sm-12">
            <div class="qm-panel">
              <volumes v-bind:surveyId="this.session.surveyId"></volumes>
            </div>
          </div>
        </div>
      </transition>

    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Rooms from './children/Rooms.vue'
import Volumes from './children/Volumes'
export default {
  name: 'Survey',
  components: {
    'volumes': Volumes,
    'rooms': Rooms
  },
  data: () => ({
    countResources: 0,
    selectedDefaultMode: null,
    hasDefaultMode: false,
    confirmClose: false,
    loading: true
  }),
  computed: {
    ...mapGetters({
      session: 'getSession',
      survey: 'getSurvey',
      resources: 'getAllResources',
      modesOptions: 'getAllModes'
    })
  },
  created: function () {
    this.$store.dispatch('loadSession').then(() => {
      this.$store.dispatch('getSurveyById', this.session.surveyId).then(() => {
        this.$store.dispatch('loadAllModes', this.session.surveyId).then(() => {
          this.$store.dispatch('loadResources', this.session.surveyId).then(() => {
            this.loading = false
            if (!!this.survey.defaultMode && this.survey.defaultMode !== null) {
              this.hasDefaultMode = true
            }
            if (this.resources.length > 0) {
              this.countResources = this.resources.length
            }
          })
        })
      })
    })
  },
  methods: {
    selectDefaultMode () {
      if (this.selectedDefaultMode !== null) {
        this.survey.defaultMode = this.selectedDefaultMode
        this.$store.dispatch('updateSurvey', this.survey).then(() => {
          this.hasDefaultMode = true
        })
      }
    },
    editSurvey () {
      this.$router.push({name: 'survey-detail'})
    },
    closeSurveyConfirm () {
      this.confirmClose = !this.confirmClose
    },
    closeSurvey () {
      this.$store.dispatch('setSurveyAsClosed', this.survey).then(() => {
        this.$router.push({ name: 'sync' })
      })
    }
  }
}
</script>

<style scoped>
  .fade-enter-active, .fade-leave-active {
    transition: opacity .3s ease;
  }
  .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
  }
</style>



// WEBPACK FOOTER //
// src/components/Survey.vue